import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Card, CardText } from 'material-ui/Card';
import compose from 'recompose/compose';
import { createSelector } from 'reselect';

import queryReducer, { SET_SORT, SET_PAGE, SORT_DESC, SORT_ASC } from 'reducers/resource/list/queryReducer';
import ViewTitle from '../layout/ViewTitle';
import Title from '../layout/Title';
import DefaultPagination from './Pagination';
import DefaultActions from './Actions';
import { crudGetList as crudGetListAction } from 'actions/dataActions';
import { changeListParams as changeListParamsAction } from 'actions/listActions';
import { saveFilter as saveFilterAction } from "actions/filterActions";

const styles = {
  noResults: { padding: 20 },
};

const sortFunc = (sortKey, sortOrder) => (a, b) => {
  const aValue = a[sortKey];
  const bValue = b[sortKey];

  if (sortOrder === SORT_DESC) {
    if (aValue > bValue) {
      return -1
    }
    if (aValue <= bValue) {
      return 1
    }
  }
  else {
    if (aValue > bValue) {
      return 1
    }
    if (aValue <= bValue) {
      return -1
    }
  }


  return 0
};

export class List extends Component {

  componentDidMount() {
    const {autoLoadData, crudGetList, resource, backendPath, filterValues, checkCanAutoLoad} = this.props;
    this.changeParams({});

    if (autoLoadData || (checkCanAutoLoad && checkCanAutoLoad(filterValues))) {
      crudGetList(resource, backendPath, filterValues);
    }
  }

  getBasePath() {
    return this.props.location.pathname;
  }

  onSearch = (filterValues) => {
    if (this.props.validateFilterForm && !this.props.validateFilterForm(filterValues)) {
      return;
    }
    let newFilterValue = {...filterValues};
    if (this.props.updateFilterValueBeforeSearch) {
      newFilterValue = this.props.updateFilterValueBeforeSearch(newFilterValue);
    }
    this.props.crudGetList(this.props.resource, this.props.backendPath, newFilterValue);
    this.props.saveFilter(this.props.resource, newFilterValue);
    this.setPage(1); // Reset page number after search
  };

  onExport = () => {
    const { data, params, exportFileName, exportHeaders, exportSources, exportSourcesTransportMap={} } = this.props;
    const headers = [exportHeaders.join(',')];

    let dataArr = Object.values(data);
    if (params.sort) {
      dataArr.sort(sortFunc(params.sort, params.order));
    }
    const contents = dataArr.map(d => exportSources.map(h => {
      const val = exportSourcesTransportMap[h] ? exportSourcesTransportMap[h](d[h]) : d[h];
      if (isNaN(val))
        return `"${val}"`;
      else
        return `="${val}"`;
    }).join(','));

    const exportData = [...headers, ...contents].join('\n');
    const link = document.createElement('a');
    link.setAttribute("href", "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURI(exportData));
    link.setAttribute("download", exportFileName);
    link.click();
  };

  /**
   * Merge list params from 3 different sources:
   *   - the query string
   *   - the params stored in the state (from previous navigation)
   *   - the props passed to the List component
   */
  getQuery() {
    const query = { ...this.props.params };
    if (!query.sort) {
      query.sort = this.props.sort.field;
      query.order = this.props.sort.order;
    }

    if (!query.perPage) {
      query.perPage = this.props.perPage;
    }
    return query;
  }

  setSort = sort => this.changeParams({ type: SET_SORT, payload: sort });

  setPage = page => this.changeParams({ type: SET_PAGE, payload: page });

  changeParams(action) {
    const newParams = queryReducer(this.getQuery(), action);
    this.props.changeListParams(this.props.resource, newParams);
  }

  render() {
    const {
      filters, pagination = <DefaultPagination />, actions = <DefaultActions />, resource,
      hasCreate, hasClearFilter, hasExport, title, data, ids, total, children, isLoading, custom_button, filterValues,
    } = this.props;
    const query = this.getQuery();
    const basePath = this.getBasePath();

    const defaultTitle = `${resource}`;
    const titleElement = <Title title={title} defaultTitle={defaultTitle}/>;

    return (
      <div className="list-page">
        <Card style={{ opacity: isLoading ? 0.8 : 1 }}>
          {actions && React.cloneElement(actions, {
            resource,
            custom_button,
            basePath,
            hasCreate,
            hasExport,
            hasClearFilter: hasClearFilter && filterValues && Object.keys(filterValues).length > 0,
            onExport: this.onExport,
          })}
          <ViewTitle title={titleElement}/>
          {filters && React.cloneElement(filters, {
            resource,
            onSubmit: this.onSearch,
          })}
          { isLoading || total > 0 ?
            <div>
              {children && React.cloneElement(children, {
                resource,
                ids,
                data,
                currentSort: { field: query.sort, order: query.order },
                basePath,
                isLoading,
                setSort: this.setSort,
              })}
              { pagination && React.cloneElement(pagination, {
                total,
                page: parseInt(query.page, 10),
                perPage: parseInt(query.perPage, 10),
                setPage: this.setPage,
              }) }
            </div>
            :
            <CardText style={styles.noResults}>{'No results'}</CardText>
          }
        </Card>
      </div>
    );
  }
}

List.propTypes = {
  // the props you can change
  title: PropTypes.any,
  filter: PropTypes.object,
  filters: PropTypes.element,
  pagination: PropTypes.element,
  actions: PropTypes.element,
  perPage: PropTypes.number,
  sort: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.string,
  }),
  children: PropTypes.element.isRequired,
  // the props managed by admin-on-rest
  changeListParams: PropTypes.func.isRequired,
  crudGetList: PropTypes.func.isRequired,
  data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  hasCreate: PropTypes.bool.isRequired,
  hasExport: PropTypes.bool.isRequired,
  ids: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  path: PropTypes.string,
  params: PropTypes.object.isRequired,
  resource: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  custom_button: PropTypes.object,
  autoLoadData: PropTypes.bool,
  hasClearFilter: PropTypes.bool,
  updateFilterValueBeforeSearch: PropTypes.func,
  validateFilterForm: PropTypes.func,
};

List.defaultProps = {
  filter: {},
  sort: {
    field: 'id',
    order: SORT_ASC,
  },
  hasExport: false,
  hasClearFilter: false,
  perPage: 10,
};

const listParamsSelector = resourceState => resourceState.list.params;
const dataSelector = (resourceState, props) => resourceState.data;

const idsSelector = createSelector(
  listParamsSelector,
  dataSelector,
  (listParams, data) => {
    if (!data) {
      return [];
    }
    const { sort, order, page, perPage } = listParams;

    let dataArr = Object.values(data);
    if (sort) {
      dataArr.sort(sortFunc(sort, order));
    }
    const start = perPage * (page - 1);
    const end = start + perPage;
    dataArr = dataArr.slice(start, end);
    return dataArr.map(item => item.id);
  }
);


function mapStateToProps(state, props) {
  const resourceState = state.admin[props.resource];
  return {
    filterValues: state.admin.filter[props.resource],
    params: resourceState.list.params,
    ids: idsSelector(resourceState, props),
    total: resourceState.list.total,
    data: resourceState.data,
    isLoading: state.admin.loading > 0,
  };
}

const enhance = compose(
  connect(
    mapStateToProps,
    {
      crudGetList: crudGetListAction,
      changeListParams: changeListParamsAction,
      saveFilter: saveFilterAction,
    },
  ),
);

export default enhance(List);



// WEBPACK FOOTER //
// ./src/components/list/List.js